// i18n
import translations from './i18n.json';
import { translate, translateInComponent } from 'infras/i18n';

import { css, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import {
  Appearance,
  black,
  gray01,
  gray03,
  gray05,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import AboutHeader from 'presentations/views/components/organisms/AboutHeader';
import Headline from 'presentations/views/components/atoms/Headline';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
  borderRadius,
  isMediaMax,
} from 'presentations/views/styles';
import GraduateText from 'presentations/views/components/atoms/GraduateText';
import Button from 'presentations/views/components/atoms/Button';
import {
  homePath,
  newsListPath,
  blogPath,
  careerListPath,
  engineeringListPath,
  careerPath,
} from 'presentations/routes/path';
import { useAboutPage } from './hooks';
import { formatToDateTime } from 'infras/datetime';

import dynamic from 'next/dynamic';
import {
  BlogListItem,
  CareerListItem,
} from 'presentations/api/schema/contents';
const AboutGlobe = dynamic(
  () => import('presentations/views/components/atoms/AboutGlobe'),
  { ssr: false },
);

interface Props {
  newsBlogs: BlogListItem[];
  engineeringBlogs: BlogListItem[];
  careerItems: CareerListItem[];
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state, actions } = useAboutPage();
  return (
    <ThemeProvider theme={{ appearance: Appearance.Dark }}>
      <Wrapper>
        <ServicePageTemplate header={<AboutHeader />} breadcrumbs={[]}>
          <Page>
            <SectionContainerWrapper>
              <SectionContainer>
                <GlobeContainer>
                  <AboutGlobe maxWidth={816} />
                </GlobeContainer>
                <TitleContainer>
                  <Title variant="h2">
                    <GraduateText>{t('Our Mission')}</GraduateText>
                  </Title>
                  <Statement variant="h1">
                    <WhiteGraduationText>{t('Mission')}</WhiteGraduationText>
                  </Statement>
                  <StateText>{t('Mission text')}</StateText>
                  <StateDescription>
                    {t('Mission description')}
                  </StateDescription>
                </TitleContainer>
              </SectionContainer>

              <SectionContainer>
                <ProductSectionTitle>
                  <GraduateText>Our Product</GraduateText>
                </ProductSectionTitle>
                <ProductContainer>
                  <ProductImage
                    src={'pages/about/device.png'}
                    dpr={2}
                    width={600}
                    height={480}
                    alt={t('Product')}
                  />
                  <ProductContent>
                    <Link href={homePath()}>
                      <ServiceImage
                        src={'logo/logo_jicoo-whB.svg'}
                        alt={translate('siteName')}
                        width={130}
                        height={34}
                        dpr={2}
                      />
                    </Link>
                    <ProductTitle>{t('Product catch')}</ProductTitle>
                    <ProductText>{t('Product text')}</ProductText>
                    <ProductButton href={homePath()} color="primary">
                      {t('Product link')}
                    </ProductButton>
                  </ProductContent>
                </ProductContainer>
              </SectionContainer>
              <SectionContainer>
                <SectionTitle>
                  <GraduateText>Newsroom</GraduateText>
                </SectionTitle>
                <NewsList>
                  {props.newsBlogs.map((blog) => (
                    <NewsListItem key={blog.id}>
                      <MediaItem href={blogPath(blog.category, blog.slug)}>
                        {blog.thumbnailUrl && (
                          <MediaImageContainer>
                            {blog.podcastUrl && (
                              <PlayImageContainer
                                data-url={blog.podcastUrl}
                                onClick={actions.onClickPlay}
                              >
                                <PlayImage
                                  loading="lazy"
                                  src={'pages/about/iconPlay.png'}
                                  alt={blog.title}
                                  width={64}
                                  height={64}
                                />
                              </PlayImageContainer>
                            )}
                            <MediaImage
                              alt={blog.title}
                              loading="lazy"
                              src={blog.thumbnailUrl}
                            />
                          </MediaImageContainer>
                        )}
                      </MediaItem>
                      <NewsListItemContent>
                        {/* <ContentCategoryLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.category.toLocaleUpperCase()}
                        </ContentCategoryLink> */}
                        <ContentTextLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.title}
                        </ContentTextLink>
                        <ContentDate>
                          {formatToDateTime(blog.createdAt)}
                        </ContentDate>
                      </NewsListItemContent>
                    </NewsListItem>
                  ))}
                </NewsList>
                <NewsListButtonContainer>
                  <ProductButton
                    size="large"
                    href={newsListPath()}
                    color="primary"
                  >
                    {t('View Newsroom')}
                  </ProductButton>
                </NewsListButtonContainer>
              </SectionContainer>
              <CompanyContainer id="corporate">
                <CompanyBgImage image={imagePath('pages/about/office.jpg')} />
                <OfficeContainer>
                  <OfficeTitle>Company</OfficeTitle>
                  <OfficeList>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('company label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {t('company text')}
                      </OfficeListItemText>
                    </OfficeListItem>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('founded label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {t('founded text')}
                      </OfficeListItemText>
                    </OfficeListItem>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('president label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {t('president text')}
                      </OfficeListItemText>
                    </OfficeListItem>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('address label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {translate('address text.0')}
                        <br />
                        {state.isJa && translate('address text.2')}
                        {translate('address text.1')}
                        {!state.isJa && `, ${translate('address text.2')}`}
                      </OfficeListItemText>
                    </OfficeListItem>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('address2 label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {t('address2 text.0')}
                        <br />
                        {t('address2 text.1')}
                        <MapLink
                          href="https://maps.app.goo.gl/Rq1SV3o35yCSJ2pr6"
                          target="_blank"
                          rel="noopener"
                        >
                          {t('address2 text.2')}
                        </MapLink>
                      </OfficeListItemText>
                    </OfficeListItem>
                    <OfficeListItem>
                      <OfficeListItemTitle>
                        {t('capital label')}
                      </OfficeListItemTitle>
                      <OfficeListItemText>
                        {t('capital text')}
                      </OfficeListItemText>
                    </OfficeListItem>
                  </OfficeList>
                </OfficeContainer>
              </CompanyContainer>

              <SectionContainer>
                <CareerTitle>
                  <GraduateText>Our Team</GraduateText>
                </CareerTitle>
                <TeamList>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((id) => (
                    <TeamListItem key={id}>
                      <TeamListItemHeader>
                        <TeamListItemImage
                          src={`pages/about/team/${t(`team_image.${id}`)}`}
                          alt={t(`team_name.${id}`)}
                          width={152}
                          height={152}
                          dpr={2}
                        />
                        <TeamListItemContent>
                          <TeamListItemTitle>
                            {t(`team_title.${id}`)}
                          </TeamListItemTitle>
                          <TeamListItemName>
                            {t(`team_name.${id}`)}
                          </TeamListItemName>
                        </TeamListItemContent>
                      </TeamListItemHeader>
                      <TeamListItemText>
                        {t(`team_text.${id}`)}
                      </TeamListItemText>
                    </TeamListItem>
                  ))}
                </TeamList>
              </SectionContainer>
              <SectionContainer>
                <ValueContainer>
                  <ValueCatch>
                    <GraduateText>OUR VALUES</GraduateText>
                  </ValueCatch>
                  <ValueList>
                    <ValueListItem>
                      <ValueTitle>
                        <WhiteGraduationText>
                          Be an Entrepreneur
                        </WhiteGraduationText>
                      </ValueTitle>
                      <ValueText>{t('ValueDescriptions.0')}</ValueText>
                    </ValueListItem>
                    <ValueListItem>
                      <ValueTitle>
                        <WhiteGraduationText>Move Fast</WhiteGraduationText>
                      </ValueTitle>
                      <ValueText>{t('ValueDescriptions.1')}</ValueText>
                    </ValueListItem>
                    <ValueListItem>
                      <ValueTitle>
                        <WhiteGraduationText>
                          Focus and Grit
                        </WhiteGraduationText>
                      </ValueTitle>
                      <ValueText>{t('ValueDescriptions.2')}</ValueText>
                    </ValueListItem>
                    <ValueListItem>
                      <ValueTitle>
                        <WhiteGraduationText>Simplify</WhiteGraduationText>
                      </ValueTitle>
                      <ValueText>{t('ValueDescriptions.3')}</ValueText>
                    </ValueListItem>
                    <ValueListItem>
                      <ValueTitle>
                        <WhiteGraduationText>One Jicoo</WhiteGraduationText>
                      </ValueTitle>
                      <ValueText>{t('ValueDescriptions.4')}</ValueText>
                    </ValueListItem>
                  </ValueList>
                </ValueContainer>
              </SectionContainer>
              <SectionContainer id="careers">
                <CareerTitle>
                  <GraduateText>Careers</GraduateText>
                </CareerTitle>
                <CareerContainer>
                  <CareerBgImage image={imagePath('pages/about/hiring.jpg')} />
                  <CareerContent>
                    <CareerContentTitle>We Are Hiring!</CareerContentTitle>
                    <CareerContentText>{t('Hiring Text')}</CareerContentText>
                    <CareerContentDescription>
                      {t('Hiring Description')}
                    </CareerContentDescription>
                    <CareerButtonContainer>
                      <CareerButton href={careerListPath()}>
                        {t('Hiring Button')}
                      </CareerButton>
                    </CareerButtonContainer>
                  </CareerContent>
                </CareerContainer>
                <NewsList>
                  {props.careerItems.map((blog) => (
                    <NewsListItem key={blog.id}>
                      <MediaItem href={careerPath(blog.slug)}>
                        {blog.thumbnailUrl && (
                          <MediaImageContainer>
                            <MediaImage
                              alt={blog.title}
                              loading="lazy"
                              src={blog.thumbnailUrl}
                            />
                          </MediaImageContainer>
                        )}
                      </MediaItem>
                      <NewsListItemContent>
                        {/* <ContentCategoryLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.category.toLocaleUpperCase()}
                        </ContentCategoryLink> */}
                        <ContentTextLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.title}
                        </ContentTextLink>
                        <ContentDate>
                          {formatToDateTime(blog.createdAt)}
                        </ContentDate>
                      </NewsListItemContent>
                    </NewsListItem>
                  ))}
                </NewsList>
                <NewsListButtonContainer>
                  <ProductButton
                    size="large"
                    href={careerListPath()}
                    color="primary"
                  >
                    {t('View Careers')}
                  </ProductButton>
                </NewsListButtonContainer>
              </SectionContainer>
              <SectionContainer id="engineering">
                <SectionTitle>
                  <GraduateText>Engineering</GraduateText>
                </SectionTitle>
                <NewsList>
                  {props.engineeringBlogs.map((blog) => (
                    <NewsListItem key={blog.id}>
                      <MediaItem href={blogPath(blog.category, blog.slug)}>
                        {blog.thumbnailUrl && (
                          <MediaImageContainer>
                            {blog.podcastUrl && (
                              <PlayImageContainer
                                data-url={blog.podcastUrl}
                                onClick={actions.onClickPlay}
                              >
                                <PlayImage
                                  loading="lazy"
                                  src={'pages/about/iconPlay.png'}
                                  alt={blog.title}
                                  width={64}
                                  height={64}
                                />
                              </PlayImageContainer>
                            )}
                            <MediaImage
                              alt={blog.title}
                              loading="lazy"
                              src={blog.thumbnailUrl}
                            />
                          </MediaImageContainer>
                        )}
                      </MediaItem>
                      <NewsListItemContent>
                        {/* <ContentCategoryLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.category.toLocaleUpperCase()}
                        </ContentCategoryLink> */}
                        <ContentTextLink
                          href={blogPath(blog.category, blog.slug)}
                        >
                          {blog.title}
                        </ContentTextLink>
                        <ContentDate>
                          {formatToDateTime(blog.createdAt)}
                        </ContentDate>
                      </NewsListItemContent>
                    </NewsListItem>
                  ))}
                </NewsList>
                <NewsListButtonContainer>
                  <ProductButton
                    size="large"
                    href={engineeringListPath()}
                    color="primary"
                  >
                    {t('View Engineering')}
                  </ProductButton>
                </NewsListButtonContainer>
              </SectionContainer>
            </SectionContainerWrapper>
          </Page>
        </ServicePageTemplate>
      </Wrapper>
    </ThemeProvider>
  );
}

const Wrapper = styled.div`
  color: ${black};
`;
const Page = styled.div`
  position: relative;
  ${mediaMax.tablet} {
    overflow: hidden;
  }
`;
const SectionContainerWrapper = styled.div`
  background-size: 100vw auto;
  color: ${black};
`;
const SectionContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 0 ${spacing(16)} ${spacing(96)};
  /* padding: ${spacing(128)} ${spacing(16)} ${spacing(96)}; */
  ${mediaMax.tablet} {
    padding: 0 ${spacing(16)} ${spacing(48)} ;
  }
  :last-of-type {
    border-bottom: 0;
  }
  position: relative;
`;
const TitleContainer = styled.div`
  display: flex;
  position: relative;
  z-index: 1;
  flex-direction: column;
  justify-content: center;
  margin: ${spacing(120)} 0 ${spacing(80)};
  ${mediaMax.tablet} {
    margin: ${spacing(80)} 0 ${spacing(64)};
  }
`;
const Title = styled(Headline)`
  display: inline-block;
  font-weight: ${fontWeight('bold')};
  text-align: center;
  letter-spacing: 0.02em;
  font-size: ${fontSize(14)};

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
  }
`;

const Statement = styled(Headline)`
  font-size: 72px;
  text-align: center;
  ${mediaMax.tablet} {
    font-size: ${fontSize(36)};
  }
`;
const StateText = styled.div`
  font-size: ${fontSize(20)};
  text-align: center;
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(20)};
  color: ${black};
`;
const StateDescription = styled.div`
  text-align: center;
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;

const CompanyContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 64px;
  position: relative;
  ${mediaMax.tablet} {
    padding-top: 0;
  }
`;
const CompanyBgImage = styled.div<{ image: string }>`
  position: absolute;
  z-index: 0;
  width: 100%;
  height: 460px;
  ${({ image }) => css`
    background-image: url(${image});
    background-repeat: no-repeat;
    background-size: cover;
  `};
  ${mediaMax.tablet} {
    height: 150px;
  }
`;
const OfficeContainer = styled.div`
  margin-top: 290px;
  border-radius: ${borderRadius(16)};
  padding: ${spacing(40)} ${spacing(56)};
  background-color: ${whiteFix};
  color: ${white};
  width: 600px;
  z-index: 1;
  ${mediaMax.tablet} {
    padding: ${spacing(24)} ${spacing(28)};
    margin: 128px ${spacing(16)} 0;
  }
`;
const OfficeTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(56)};
  margin-bottom: ${spacing(28)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(32)};
  }
`;
const OfficeList = styled.div``;
const OfficeListItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const OfficeListItemTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  width: 160px;
`;
const OfficeListItemText = styled.div`
  flex: 1;
`;

const NewsList = styled.div`
  display: grid;
  grid-template-columns: 300px 300px 300px;
  grid-gap: ${spacing(40)};
  justify-content: center;
  ${mediaMax.desktop} {
    grid-template-columns: 400px 400px;
  }
  ${mediaMax.tablet} {
    display: block;
  }
`;
const NewsListItem = styled.div`
  background: ${gray05};
  border-radius: ${borderRadius(16)};
  overflow: hidden;
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const NewsListButtonContainer = styled.div`
  text-align: center;
  margin-top: ${spacing(16)};
`;
const MediaItem = styled(Link)`
  img {
    max-width: 100%;
  }
`;
const NewsListItemContent = styled.div`
  margin: ${spacing(16)};
`;
const ContentCategoryLink = styled(Link)`
  display: inline-block;
  font-weight: ${fontWeight('bold')};
  color: ${whiteFix};
  background-color: ${primary};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(13)};
  padding: 0 ${spacing(8)};
  border-radius: ${borderRadius(4)};
  margin-bottom: ${spacing(8)};
`;
const ContentTextLink = styled(Link)`
  display: block;
  font-weight: ${fontWeight('bold')};
  color: ${black};
`;
const ContentDate = styled.div`
  font-weight: ${fontWeight('bold')};
  color: ${gray03};
  font-size: ${fontSize(12)};
  margin-top: ${spacing(4)};
`;
const MediaImageContainer = styled.div`
  margin-bottom: ${spacing(16)};
  width: 100%;
  overflow: hidden;
  position: relative;
  height: 197px;
`;
const MediaImage = styled(Image)`
  width: 100%;
  height: 197px;
  object-fit: cover;
  border-radius: 0;
  z-index: 0;
`;

const MapLink = styled(Link)`
  display: block;
`;
const GlobeContainer = styled.div`
  position: absolute;
  top: -280px;
  left: 0;
  right: 0;
  height: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  /* height: 400px; */
  /* overflow: hidden; */
  z-index: 0;
  ${mediaMax.tablet} {
    top: -120px;
  }
  ${mediaMax.smartphone} {
    top: -80px;
  }
`;

const WhiteGraduationText = styled.span`
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255) 0%,
    rgba(255, 255, 255) 50%,
    rgba(174, 174, 174) 100%
  );
  background-clip: text;
  color: transparent;
`;
const ValueContainer = styled.div`
  border-radius: ${borderRadius(48)};
  padding: ${spacing(56)} 0;
  background: linear-gradient(
    180deg,
    rgba(33, 43, 57) 0%,
    rgba(17, 23, 31) 100%
  );
  ${mediaMax.tablet} {
    padding: ${spacing(28)} 0;
    border-radius: ${borderRadius(16)};
  }
`;
const ValueCatch = styled.div`
  padding: 0 ${spacing(72)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    padding: 0 ${spacing(24)};
  }
`;
const ValueList = styled.div`
  padding: 0 ${spacing(96)};
  ${mediaMax.tablet} {
    padding: 0 ${spacing(24)};
  }
`;
const ValueListItem = styled.div`
  margin-bottom: ${spacing(8)};
`;
const ValueTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(48)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;
const ValueText = styled.p``;
const SectionTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(56)};
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(56)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(32)};
  }
`;
const CareerTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(56)};
  display: flex;
  justify-content: flex-start;
  margin-top: ${spacing(72)};
  margin-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(32)};
    margin-top: ${spacing(36)};
  }
`;
const CareerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing(16)};
  margin-bottom: ${spacing(32)};
  ${mediaMax.tablet} {
    display: block;
    border-radius: ${borderRadius(16)};
    overflow: hidden;
  }
`;
const CareerBgImage = styled.div<{ image: string }>`
  border-radius: ${borderRadius(16)};
  position: absolute;
  top: 0;
  left: 0vmax;
  z-index: 0;
  width: 600px;
  height: 400px;
  ${({ image }) => css`
    background-image: url(${image});
    background-repeat: no-repeat;
    background-size: cover;
  `};
  ${mediaMax.tablet} {
    position: static;
    border-radius: 0;
    width: 100%;
    height: 240px;
  }
`;
const CareerContent = styled.div`
  margin-top: ${spacing(80)};
  border-radius: ${borderRadius(16)};
  padding: ${spacing(48)} ${spacing(48)};
  z-index: 1;
  background: linear-gradient(
    73.82deg,
    #11d79b -24.74%,
    #11bdd7 16.25%,
    #0054d1 79.08%,
    #8e00d1 113.33%
  );
  max-width: 440px;
  ${mediaMax.tablet} {
    margin-top: 0;
    border-radius: 0;
    padding: ${spacing(24)} ${spacing(24)};
  }
`;
const CareerContentTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(44)};
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(28)};
  }
`;
const CareerContentText = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  margin-bottom: ${spacing(16)};
`;
const CareerContentDescription = styled.div``;
const CareerButtonContainer = styled.div``;
const CareerButton = styled(Button)`
  margin-top: ${spacing(24)};
  height: 62px;
  min-width: 168px;
  color: ${black};
  background-color: ${white};
  border-radius: ${borderRadius(48)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const ProductContainer = styled.div`
  display: flex;
  justify-content: space-between;
  ${mediaMax.tablet} {
    display: block;
  }
`;
const ProductImage = styled(Image)`
  width: 600px;
  height: auto;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const ProductContent = styled.div`
  flex: 1;
  margin-top: ${spacing(120)};
  margin-left: ${spacing(8)};
`;
const ProductTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(22)};
  margin-top: ${spacing(16)};
`;
const ProductText = styled.div`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(16)};
`;
const ProductButton = styled(Button)`
  margin-top: ${spacing(24)};
  height: 62px;
  min-width: 168px;
  border-radius: ${borderRadius(48)};
`;
const ServiceImage = styled(Image)`
  width: 130px;
  height: auto;
`;
const ProductSectionTitle = styled(SectionTitle)`
  margin-bottom: 0;
`;

const PlayImageContainer = styled.div`
  cursor: pointer;
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const PlayImage = styled(Image)``;

const TeamList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${spacing(24)};
  row-gap: ${spacing(40)};
  ${mediaMax.tablet} {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;
const TeamListItem = styled.div``;
const TeamListItemHeader = styled.div`
  display: flex;
  margin-bottom: ${spacing(16)};
`;
const TeamListItemImage = styled(Image)`
  display: block;
  border-radius: 50%;
  width: 152px;
  height: 152px;
  margin-right: ${spacing(16)};
  ${mediaMax.tablet} {
    width: 80px;
    height: 80px;
  }
`;
const TeamListItemContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const TeamListItemTitle = styled.div`
  color: ${gray01};
`;
const TeamListItemName = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(18)};
`;
const TeamListItemText = styled.div``;
